import { formatRevealedCpr } from '@/lib/cprDisplay';
import { chartCaption, chartHasFigure } from '@/lib/patientReports/chartFigure';
import type { PatientCardSource } from '@/lib/patientReports/model';
import type {
  ContentPiece,
  PlaceholderDictionary,
  PlaceholderReplacement,
} from '@/lib/patientReports/docxTemplate';
import type {
  CprPreviewAccess,
  ReportPreviewModel,
  ReportSection,
} from '@/lib/patientReports/template';
import { headingStyleOf, tocEntries } from '@/lib/patientReports/template';

/**
 * Stable tokens a clinic admin pastes into a Word layout.
 * The strings are the contract. Descriptions live in i18n.
 * Nothing here is a patient value.
 */

export type PlaceholderGroup = 'report' | 'patient' | 'content';

export type PlaceholderDefinition = {
  token: string;
  group: PlaceholderGroup;
  descriptionKey: string;
  wordField?: 'PAGE' | 'NUMPAGES';
  /** Cover text is composed on the PDF. It is not a token to paste into the Word file. */
  catalog?: boolean;
};

export const PATIENT_REPORT_PLACEHOLDERS: readonly PlaceholderDefinition[] = [
  {
    token: '[Rapportnavn]',
    group: 'report',
    descriptionKey: 'patientReports.placeholder_desc.report_name',
  },
  {
    token: '[Behandler]',
    group: 'report',
    descriptionKey: 'patientReports.placeholder_desc.author',
  },
  {
    token: '[Rapportdato]',
    group: 'report',
    descriptionKey: 'patientReports.placeholder_desc.report_date',
  },
  {
    token: '[Kliniknavn]',
    group: 'report',
    descriptionKey: 'patientReports.placeholder_desc.clinic_name',
  },
  {
    token: '[Forsidetitel]',
    group: 'report',
    descriptionKey: 'patientReports.placeholder_desc.cover_title',
    catalog: false,
  },
  {
    token: '[Forsidetekst]',
    group: 'report',
    descriptionKey: 'patientReports.placeholder_desc.cover_body',
    catalog: false,
  },
  {
    token: '[Sidetal]',
    group: 'report',
    descriptionKey: 'patientReports.placeholder_desc.page',
    wordField: 'PAGE',
  },
  {
    token: '[Total sider]',
    group: 'report',
    descriptionKey: 'patientReports.placeholder_desc.pages',
    wordField: 'NUMPAGES',
  },
  {
    token: '[Patientnavn]',
    group: 'patient',
    descriptionKey: 'patientReports.placeholder_desc.patient_name',
  },
  {
    token: '[CPR-nr.]',
    group: 'patient',
    descriptionKey: 'patientReports.placeholder_desc.cpr',
  },
  {
    token: '[CPR]',
    group: 'patient',
    descriptionKey: 'patientReports.placeholder_desc.cpr_alias',
  },
  {
    token: '[E-mail]',
    group: 'patient',
    descriptionKey: 'patientReports.placeholder_desc.email',
  },
  {
    token: '[Telefon]',
    group: 'patient',
    descriptionKey: 'patientReports.placeholder_desc.phone',
  },
  {
    token: '[Kontakt]',
    group: 'patient',
    descriptionKey: 'patientReports.placeholder_desc.contact',
  },
  {
    token: '[Adresse]',
    group: 'patient',
    descriptionKey: 'patientReports.placeholder_desc.address',
  },
  {
    token: '[Stilling]',
    group: 'patient',
    descriptionKey: 'patientReports.placeholder_desc.job',
  },
  {
    token: '[CAVE]',
    group: 'patient',
    descriptionKey: 'patientReports.placeholder_desc.cave',
  },
  {
    token: '[Noter]',
    group: 'patient',
    descriptionKey: 'patientReports.placeholder_desc.notes',
  },
  {
    token: '[Diagnose]',
    group: 'patient',
    descriptionKey: 'patientReports.placeholder_desc.diagnosis',
  },
  {
    token: '[Sprog]',
    group: 'patient',
    descriptionKey: 'patientReports.placeholder_desc.locale',
  },
  {
    token: '[Indholdsfortegnelse]',
    group: 'content',
    descriptionKey: 'patientReports.placeholder_desc.toc',
  },
  {
    token: '[Journal]',
    group: 'content',
    descriptionKey: 'patientReports.placeholder_desc.journal',
  },
  {
    token: '[Markeringer]',
    group: 'content',
    descriptionKey: 'patientReports.placeholder_desc.markings',
  },
  {
    token: '[Spørgeskema]',
    group: 'content',
    descriptionKey: 'patientReports.placeholder_desc.questionnaire',
  },
  {
    token: '[Filer]',
    group: 'content',
    descriptionKey: 'patientReports.placeholder_desc.files',
  },
  {
    token: '[Tekst]',
    group: 'content',
    descriptionKey: 'patientReports.placeholder_desc.text',
  },
  {
    token: '[Indhold]',
    group: 'content',
    descriptionKey: 'patientReports.placeholder_desc.content',
  },
];

export const CPR_PLACEHOLDER_TOKENS = ['[CPR-nr.]', '[CPR]'] as const;

/** Tokens a clinic pastes. Cover lines are composed on the PDF and are not tokens. */
export function catalogPlaceholders(): readonly PlaceholderDefinition[] {
  return PATIENT_REPORT_PLACEHOLDERS.filter((item) => item.catalog !== false);
}

export type PlaceholderFillContext = {
  reportTitle: string;
  authorName: string;
  clinicName: string;
  /** Clock used for a draft. A finished report ignores this and prints `finishedAt`. */
  generatedAt: Date;
  /**
   * Completion instant stored on the report when it is færdiggjort.
   * Empty on a draft, so the date is not frozen.
   */
  finishedAt?: Date | null;
  /** Finished report. Never print "Udkast af", even if the stored instant is missing. */
  completed?: boolean;
  patient: PatientCardSource | null;
  cprAccess: CprPreviewAccess;
  cpr: string | null;
  cprHidden: string;
  cprLoading: string;
  coverTitle: string;
  coverBody: string;
  journal: string;
  markings: string;
  questionnaire: string;
  files: string;
  text: string;
  content: string;
  toc: string;
  /** Bare headings, one per line, parallel to `toc`. Omitted when the label is the heading. */
  tocTitles?: string;
  /** Heading style of each contents line, parallel to `toc`. Missing means bold. */
  tocStyles?: string;
  /** Section text with headings kept separate so Word and PDF can style them. */
  contentPieces?: readonly ContentPiece[];
  /** Questionnaire sections only, including chart figures. Used when `[Spørgeskema]` is its own paragraph. */
  questionnairePieces?: readonly ContentPiece[];
};

export function formatReportDate(date: Date): string {
  return new Intl.DateTimeFormat('da-DK', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

/** Danish wording printed in the report itself. The template tokens are Danish. */
export const DRAFT_REPORT_DATE_LABEL = 'Udkast af';

function validInstant(value: Date | null | undefined): Date | null {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return null;
  return value;
}

/**
 * Finished reports print the stored completion date.
 * Drafts print "Udkast af" plus the day the file is generated. That day is not stored.
 */
export function reportDateText(input: {
  generatedAt: Date;
  finishedAt?: Date | null;
  completed?: boolean;
}): string {
  const finished = validInstant(input.finishedAt);
  if (finished) return formatReportDate(finished);
  if (input.completed) return '';
  const day = validInstant(input.generatedAt) ?? new Date();
  return `${DRAFT_REPORT_DATE_LABEL} ${formatReportDate(day)}`;
}

/** Dates passed into placeholder fill for a saved report, or for the finish action itself. */
export function reportDateFields(input: {
  status: 'draft' | 'finished';
  finishedAt?: string | null;
  /** Set while the finish PDF is built, before the row is locked. */
  completingAt?: Date | null;
}): { generatedAt: Date; finishedAt: Date | null; completed: boolean } {
  const completing = validInstant(input.completingAt);
  if (completing) {
    return { generatedAt: completing, finishedAt: completing, completed: true };
  }
  if (input.status === 'finished') {
    const stored = input.finishedAt ? validInstant(new Date(input.finishedAt)) : null;
    if (stored) return { generatedAt: stored, finishedAt: stored, completed: true };
    return { generatedAt: new Date(0), finishedAt: null, completed: true };
  }
  return { generatedAt: new Date(), finishedAt: null, completed: false };
}

/** Draft previews take the clock at generation. A stored completion date is left alone. */
export function withReportDateAtGeneration(
  source: PlaceholderFillContext,
  now = new Date(),
): PlaceholderFillContext {
  const finished = validInstant(source.finishedAt);
  if (finished) return { ...source, finishedAt: finished, completed: true };
  if (source.completed) return { ...source, finishedAt: null, completed: true };
  return { ...source, generatedAt: now, finishedAt: null, completed: false };
}

/** Stable within a calendar day for drafts, and stable forever once the report is finished. */
export function reportDateRevisionKey(source: PlaceholderFillContext, now = new Date()): string {
  const finished = validInstant(source.finishedAt);
  if (finished) return `finished:${finished.toISOString()}`;
  if (source.completed) return 'finished:missing';
  const day = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
  return `draft:${day}`;
}

export function placeholderSectionTexts(
  sections: readonly ReportSection[],
  model: ReportPreviewModel,
): Pick<
  PlaceholderFillContext,
  | 'journal'
  | 'markings'
  | 'questionnaire'
  | 'files'
  | 'text'
  | 'content'
  | 'toc'
  | 'tocTitles'
  | 'tocStyles'
  | 'contentPieces'
  | 'questionnairePieces'
> {
  const plain = (index: number): string => {
    const block = model.blocks[index];
    if (!block) return '';
    const charts = block.charts
      .map((chart) => chartCaption(chart))
      .filter((line) => line.trim().length > 0)
      .join('\n');
    return [block.heading.trim(), block.body.trim(), charts.trim()].filter(Boolean).join('\n');
  };
  const pick = (kind: ReportSection['kind']) =>
    sections
      .map((section, index) => (section.kind === kind ? plain(index) : ''))
      .filter(Boolean)
      .join('\n\n');
  const headings = sections.map(
    (section, index) => model.blocks[index]?.heading.trim() || section.heading.trim(),
  );
  const contentPieces: ContentPiece[] = [];
  const questionnairePieces: ContentPiece[] = [];
  let breakBefore = false;
  let questionnaireBreak = false;
  const pushGap = () => {
    if (contentPieces.length === 0) return;
    contentPieces.push({ type: 'text', text: breakBefore ? '\f' : '\n\n' });
  };
  const pushQuestionnaireGap = () => {
    if (questionnairePieces.length === 0) return;
    questionnairePieces.push({ type: 'text', text: questionnaireBreak ? '\f' : '\n\n' });
  };
  for (let index = 0; index < sections.length; index += 1) {
    const section = sections[index];
    if (!section) continue;
    if (section.kind === 'toc') {
      const entries = tocEntries(sections, section.numbering, headings);
      const heading = headings[index] ?? '';
      if (!heading && entries.length === 0) continue;
      pushGap();
      contentPieces.push({
        type: 'toc',
        heading,
        headingStyle: headingStyleOf(section.headingStyle),
        entries,
      });
      breakBefore = section.pageBreakAfter === true;
      continue;
    }
    const block = model.blocks[index];
    const heading = block?.heading.trim() ?? '';
    const charts = (block?.charts ?? []).filter(chartHasFigure);
    const body = block?.body.trim() ?? '';
    if (!heading && !body && charts.length === 0) continue;
    const pieces: ContentPiece[] = [];
    if (heading) {
      pieces.push({
        type: 'heading',
        text: heading,
        style: headingStyleOf(section.headingStyle),
      });
    }
    if (body) pieces.push({ type: 'text', text: heading ? `\n${body}` : body });
    for (const chart of charts) {
      pieces.push({
        type: 'chart',
        ...(chart.id ? { id: chart.id } : {}),
        ...(chart.kind ? { kind: chart.kind } : {}),
        label: chart.label.trim(),
        value: chart.value,
        max: chart.max,
        ...(chart.points ? { points: chart.points } : {}),
      });
    }
    pushGap();
    contentPieces.push(...pieces);
    breakBefore = section.pageBreakAfter === true;
    if (section.kind === 'questionnaire') {
      pushQuestionnaireGap();
      questionnairePieces.push(...pieces);
      questionnaireBreak = section.pageBreakAfter === true;
    }
  }
  const firstToc = sections.find((section) => section.kind === 'toc');
  const tokenEntries = firstToc ? tocEntries(sections, firstToc.numbering, headings) : [];
  return {
    journal: pick('journal'),
    markings: pick('markings'),
    questionnaire: pick('questionnaire'),
    files: pick('file'),
    text: pick('text'),
    content: flattenContent(contentPieces),
    toc: tokenEntries.map((entry) => entry.label).join('\n'),
    tocTitles: tokenEntries.map((entry) => entry.title).join('\n'),
    tocStyles: tokenEntries.map((entry) => entry.style).join('\n'),
    contentPieces: contentPieces.length > 0 ? contentPieces : undefined,
    questionnairePieces: questionnairePieces.length > 0 ? questionnairePieces : undefined,
  };
}

export function flattenContent(pieces: readonly ContentPiece[]): string {
  return pieces
    .map((piece) => {
      if (piece.type === 'text' || piece.type === 'heading') return piece.text;
      if (piece.type === 'chart') return chartCaption(piece);
      return [piece.heading.trim(), ...piece.entries.map((entry) => entry.label)]
        .filter(Boolean)
        .join('\n');
    })
    .join('');
}

export function reportPlaceholderContext(input: {
  reportTitle: string;
  authorName: string;
  clinicName: string;
  generatedAt: Date;
  finishedAt?: Date | null;
  completed?: boolean;
  patient: PatientCardSource | null;
  cprAccess: PlaceholderFillContext['cprAccess'];
  cpr: string | null;
  cprHidden: string;
  cprLoading: string;
  coverTitle: string;
  coverBody: string;
  sections: readonly ReportSection[];
  model: ReportPreviewModel;
}): PlaceholderFillContext {
  return {
    reportTitle: input.reportTitle,
    authorName: input.authorName,
    clinicName: input.clinicName,
    generatedAt: input.generatedAt,
    finishedAt: input.finishedAt ?? null,
    completed: input.completed === true || validInstant(input.finishedAt) !== null,
    patient: input.patient,
    cprAccess: input.cprAccess,
    cpr: input.cpr,
    cprHidden: input.cprHidden,
    cprLoading: input.cprLoading,
    coverTitle: input.coverTitle,
    coverBody: input.coverBody,
    ...placeholderSectionTexts(input.sections, input.model),
  };
}

/**
 * Replace the same [placeholder] tokens a Word template uses.
 * Page-number fields stay as tokens: they only become Word fields inside a .docx.
 * Empty values remove the token, matching the Word fill.
 */
export function fillPlaceholderText(source: string, dictionary: PlaceholderDictionary): string {
  if (!source.includes('[')) return source;
  const tokens = [...dictionary.keys()].sort((a, b) => b.length - a.length);
  let out = '';
  for (let index = 0; index < source.length; ) {
    const token =
      source[index] === '[' ? tokens.find((item) => source.startsWith(item, index)) : undefined;
    if (!token) {
      out += source[index];
      index += 1;
      continue;
    }
    const value = dictionary.get(token);
    if (value?.type === 'text') out += value.text;
    else if (value?.type === 'toc') out += value.entries.map((entry) => entry.label).join('\n');
    else out += token;
    index += token.length;
  }
  return out;
}

export function buildPlaceholderDictionary(context: PlaceholderFillContext): PlaceholderDictionary {
  const patient = context.patient;
  const cpr = cprText(context);
  const contact = [patient?.email, patient?.phone]
    .filter((part): part is string => Boolean(part))
    .join(', ');
  const text: Record<string, string> = {
    '[Rapportnavn]': context.reportTitle.trim(),
    '[Behandler]': context.authorName.trim(),
    '[Rapportdato]': reportDateText(context),
    '[Kliniknavn]': context.clinicName.trim(),
    '[Forsidetitel]': context.coverTitle.trim(),
    '[Forsidetekst]': context.coverBody.trim(),
    '[Patientnavn]': patient?.name?.trim() ?? '',
    '[CPR-nr.]': cpr,
    '[CPR]': cpr,
    '[E-mail]': patient?.email?.trim() ?? '',
    '[Telefon]': patient?.phone?.trim() ?? '',
    '[Kontakt]': contact,
    '[Adresse]': patient?.address?.trim() ?? '',
    '[Stilling]': patient?.jobTitle?.trim() ?? '',
    '[CAVE]': patient?.cave?.trim() ?? '',
    '[Noter]': patient?.notes?.trim() ?? '',
    '[Diagnose]': patient?.diagnosis?.trim() ?? '',
    '[Sprog]': patient?.preferredLocale?.trim() ?? '',
    '[Journal]': context.journal.trim(),
    '[Markeringer]': context.markings.trim(),
    '[Spørgeskema]': context.questionnaire.trim(),
    '[Filer]': context.files.trim(),
    '[Tekst]': context.text.trim(),
    '[Indhold]': context.content.trim(),
    '[Indholdsfortegnelse]': context.toc.trim(),
  };
  const dictionary = new Map<string, PlaceholderReplacement>();
  for (const placeholder of PATIENT_REPORT_PLACEHOLDERS) {
    if (placeholder.token === '[Indholdsfortegnelse]') {
      const labels = context.toc
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);
      const titles = (context.tocTitles ?? '').split('\n');
      const styles = (context.tocStyles ?? '').split('\n');
      dictionary.set(placeholder.token, {
        type: 'toc',
        entries: labels.map((label, index) => ({
          label,
          title: titles[index]?.trim() || label,
          style: headingStyleOf(styles[index]),
        })),
      });
      continue;
    }
    if (
      placeholder.token === '[Indhold]' &&
      context.contentPieces &&
      context.contentPieces.length > 0
    ) {
      dictionary.set(placeholder.token, { type: 'flow', pieces: context.contentPieces });
      continue;
    }
    if (
      placeholder.token === '[Spørgeskema]' &&
      context.questionnairePieces &&
      context.questionnairePieces.some((piece) => piece.type === 'chart')
    ) {
      dictionary.set(placeholder.token, { type: 'flow', pieces: context.questionnairePieces });
      continue;
    }
    if (placeholder.wordField) {
      dictionary.set(placeholder.token, { type: 'field', instruction: placeholder.wordField });
      continue;
    }
    dictionary.set(placeholder.token, { type: 'text', text: text[placeholder.token] ?? '' });
  }
  return dictionary;
}

function cprText(context: PlaceholderFillContext): string {
  if (context.cprAccess === 'hidden') return context.cprHidden;
  if (context.cprAccess === 'loading') return context.cprLoading;
  if (!context.cpr) return '';
  return formatRevealedCpr(context.cpr);
}
